import { useState, useCallback, useRef } from "react"
import {
  fetchDocuments, uploadDocument, updateDocument, deleteDocument,
} from "../api/documentsApi"

// Fetches a scoped, filtered list of documents. `scope` is { type, id } or null
// (top-level library). Filters: { category, folder_id, documentable_type, expiring }.
export function useDocuments(scope) {
  const [data, setData] = useState(null) // { documents, meta }
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const abortRef = useRef(null)

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    setLoading(true)
    setError(null)
    try {
      setData(await fetchDocuments(scope, filters))
    } catch (err) {
      if (err.name !== "AbortError" && err.code !== "ERR_CANCELED") setError(err)
    } finally {
      setLoading(false)
    }
    // scope is an object literal at the call site; depend on its identity parts.
  }, [scope?.type, scope?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error, fetch, refresh: fetch }
}

export function useUploadDocument(scope) {
  const [uploading, setUploading] = useState(false)

  const upload = useCallback(async (file, metadata) => {
    setUploading(true)
    try {
      return await uploadDocument(scope, file, metadata)
    } finally {
      setUploading(false)
    }
  }, [scope?.type, scope?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return { upload, uploading }
}

export function useUpdateDocument() {
  return useCallback((id, metadata) => updateDocument(id, metadata), [])
}

export function useDeleteDocument() {
  return useCallback((id) => deleteDocument(id), [])
}
